import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Support, SupportStatus } from "@models/support";
import { SupportsService } from "@services/supports.service";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { StatusSummaryCardComponent } from "../../shared/components/cards/status-summary-card/status-summary-card.component";
import { PaginatorReportComponent } from "../../shared/components/paginator-report/paginator-report.component";
import { NewSupportDialogComponent } from "./new-support-dialog/new-support-dialog.component";
import { SupportTableComponent } from "./support-table/support-table.component";

@Component({
  selector: "app-support-page",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppLayoutComponent,
    PageHeaderComponent,
    ButtonComponent,
    PaginatorReportComponent,
    StatusSummaryCardComponent,
    SupportTableComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SkeletonModule,
  ],
  templateUrl: "./support-page.component.html",
  styleUrl: "./support-page.component.scss",
})
export class SupportPageComponent {
  private readonly _supportsService = inject(SupportsService);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);

  readonly supportStatusEnum = SupportStatus;

  tableFilter = new FormControl("", { nonNullable: true });

  activeStatusFilter: SupportStatus | null = null;

  readonly isLoading = signal(false);

  private readonly _page$ = new BehaviorSubject<PaginatorState | null>(null);
  private readonly _statusFilter$ = new BehaviorSubject<SupportStatus | null>(
    null,
  );
  private readonly _refresh$ = new BehaviorSubject<void>(undefined);

  // Mínimo de caracteres para acionar a busca no servidor.
  private readonly _minSearchLength = 3;

  private readonly _search$ = this.tableFilter.valueChanges.pipe(
    startWith(""),
    debounceTime(500),
    map((value) => value.trim()),
    // Só consulta o endpoint quando o campo está vazio (reset) ou tem
    // ao menos `_minSearchLength` caracteres.
    filter(
      (value) => value.length === 0 || value.length >= this._minSearchLength,
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  supports$ = combineLatest([
    this._page$,
    this._search$,
    this._statusFilter$,
    this._refresh$,
  ]).pipe(
    switchMap(([page, search, status]) => {
      this.isLoading.set(true);
      return this._supportsService
        .getSupports(page, search, status)
        .pipe(finalize(() => this.isLoading.set(false)));
    }),
  );

  // O resumo NÃO recebe status (senão zera os outros cards); só busca.
  summary$ = combineLatest([this._search$, this._refresh$]).pipe(
    switchMap(([search]) => this._supportsService.getSummary(search)),
  );

  get hasActiveFilters(): boolean {
    return (
      this.activeStatusFilter !== null ||
      this.tableFilter.value.trim().length >= this._minSearchLength
    );
  }

  // Exibe a dica quando há texto insuficiente para acionar a busca (1–2 chars).
  get showSearchHint(): boolean {
    const length = this.tableFilter.value.trim().length;
    return length > 0 && length < this._minSearchLength;
  }

  handlePageChange(page: PaginatorState) {
    this._page$.next(page);
  }

  handleStatusFilter(status: SupportStatus) {
    if (this.activeStatusFilter === status) {
      this.activeStatusFilter = null;
      this._statusFilter$.next(null);
    } else {
      this.activeStatusFilter = status;
      this._statusFilter$.next(status);
    }
  }

  clearFilters() {
    this.tableFilter.setValue("");
    this.activeStatusFilter = null;
    this._statusFilter$.next(null);
  }

  handleViewDetails(support: Support) {
    this._router.navigate(["/support", support.id]);
  }

  openNewSupportDialog() {
    this._dialog
      .open(NewSupportDialogComponent, { autoFocus: false })
      .afterClosed()
      .pipe(
        filter((created): created is Support => !!created),
        tap(() => this._refresh$.next()),
      )
      .subscribe();
  }
}
